// Global Irrigation Context - Syncs across all irrigation components
import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
// aria-label: design-system-bypass 

import { irrigationReminderService, IrrigationSchedule, IrrigationInput } from '../services/irrigationReminderService';
// aria-label: design-system-bypass 


// Field data interface
export interface Field {
    id: string;
    name: string;
    nameHi: string;
    area: number; // acres
    cropType: string;
    soilType: 'clay' | 'loamy' | 'sandy' | 'black' | 'red';
    cropStage: 'seedling' | 'vegetative' | 'flowering' | 'fruiting' | 'maturity';
    lastIrrigation: Date;
    soilMoisture: number;
    sensorConnected: boolean;
}

// Irrigation event for history
export interface IrrigationEvent {
    id: string;
    fieldId: string;
    timestamp: Date;
    waterUsed: number;
    duration: number;
    method: 'manual' | 'scheduled' | 'sensor-triggered';
}

// Weather data for recommendations
export interface WeatherData {
    temperature: number;
    humidity: number;
    rainProbability: number;
    forecast: { date: Date; rain: number; temp: number }[];
}

// Context state
interface IrrigationContextState {
    // Fields
    fields: Field[];
    selectedFieldId: string | null;

    // Schedules
    schedules: IrrigationSchedule[];

    // History
    recentEvents: IrrigationEvent[];

    // Weather
    weather: WeatherData | null;

    // Settings
    soundEnabled: boolean;
    vibrationEnabled: boolean;
    autoScheduleEnabled: boolean;

    // Stats
    totalWaterSaved: number;
    activeReminders: number;
}

interface IrrigationContextActions {
    // Field actions
    selectField: (fieldId: string) => void;
    updateFieldMoisture: (fieldId: string, moisture: number) => void;
    updateFieldCropStage: (fieldId: string, stage: Field['cropStage']) => void;
    recordIrrigation: (fieldId: string, waterUsed: number, duration: number) => void;

    // Schedule actions
    addSchedule: (schedule: Omit<IrrigationSchedule, 'id'>) => void;
    removeSchedule: (scheduleId: string) => void;
    toggleSchedule: (scheduleId: string) => void;

    // Recommendation
    getRecommendation: (fieldId: string) => {
        nextDate: Date;
        waterRequired: number;
        aiRecommendation: string;
        urgency: 'low' | 'medium' | 'high' | 'critical';
    } | null;

    // Settings
    toggleSound: () => void;
    toggleVibration: () => void;
    toggleAutoSchedule: () => void;

    // Notifications
    testNotification: () => void;
}

type IrrigationContextType = IrrigationContextState & IrrigationContextActions;

const IrrigationContext = createContext<IrrigationContextType | null>(null);

// Demo data
const DEMO_FIELDS: Field[] = [
    { id: 'field-1', name: 'North Field', nameHi: 'उत्तर खेत', area: 2.5, cropType: 'Wheat', soilType: 'loamy', cropStage: 'vegetative', lastIrrigation: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000), soilMoisture: 42, sensorConnected: true },
    { id: 'field-2', name: 'South Field', nameHi: 'दक्षिण खेत', area: 3.0, cropType: 'Rice', soilType: 'clay', cropStage: 'flowering', lastIrrigation: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000), soilMoisture: 55, sensorConnected: true },
    { id: 'field-3', name: 'East Plot', nameHi: 'पूर्वी भूखंड', area: 1.5, cropType: 'Cotton', soilType: 'black', cropStage: 'seedling', lastIrrigation: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000), soilMoisture: 38, sensorConnected: false },
];

const DEMO_WEATHER: WeatherData = {
    temperature: 32,
    humidity: 55,
    rainProbability: 15,
    forecast: [
        { date: new Date(), rain: 0, temp: 32 },
        { date: new Date(Date.now() + 24 * 60 * 60 * 1000), rain: 10, temp: 30 },
        { date: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000), rain: 80, temp: 28 },
    ]
};

export function IrrigationProvider({ children }: { children: ReactNode }) {
    // State
    const [fields, setFields] = useState<Field[]>(DEMO_FIELDS);
    const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);
    const [schedules, setSchedules] = useState<IrrigationSchedule[]>([]);
    const [recentEvents, setRecentEvents] = useState<IrrigationEvent[]>([]);
    const [weather] = useState<WeatherData>(DEMO_WEATHER);
    const [soundEnabled, setSoundEnabled] = useState(true);
    const [vibrationEnabled, setVibrationEnabled] = useState(true);
    const [autoScheduleEnabled, setAutoScheduleEnabled] = useState(true);
    const [totalWaterSaved, setTotalWaterSaved] = useState(12500);

    // Listen for irrigation reminder events
    useEffect(() => {
        const handleReminder = (event: CustomEvent) => {
            console.log('Irrigation reminder received:', event.detail);
            // Could trigger UI notification here
        };

        window.addEventListener('irrigationReminder', handleReminder as EventListener);
        return () => window.removeEventListener('irrigationReminder', handleReminder as EventListener);
    }, []);

    // Auto-schedule based on sensor data
    useEffect(() => {
        if (!autoScheduleEnabled) return;

        fields.forEach(field => {
            if (field.sensorConnected && field.soilMoisture < 25) {
                // Critical moisture - auto-schedule if not already scheduled
                const existingSchedule = schedules.find(s => s.fieldId === field.id && s.isEnabled);
                if (!existingSchedule) {
                    const rec = getRecommendation(field.id);
                    if (rec && rec.urgency === 'critical') {
                        addSchedule({
                            fieldId: field.id,
                            fieldName: field.name,
                            cropType: field.cropType,
                            scheduledTime: rec.nextDate,
                            duration: 45,
                            waterAmount: rec.waterRequired,
                            frequency: 'custom',
                            aiRecommendation: rec.aiRecommendation,
                            isEnabled: true,
                        });
                    }
                }
            }
        });
    }, [fields, autoScheduleEnabled]);

    // Actions
    const selectField = useCallback((fieldId: string) => {
        setSelectedFieldId(fieldId);
    }, []);

    const updateFieldMoisture = useCallback((fieldId: string, moisture: number) => {
        setFields(prev => prev.map(f =>
            f.id === fieldId ? { ...f, soilMoisture: moisture } : f
        ));
    }, []);

    const updateFieldCropStage = useCallback((fieldId: string, stage: Field['cropStage']) => {
        setFields(prev => prev.map(f =>
            f.id === fieldId ? { ...f, cropStage: stage } : f
        ));
    }, []);

    const recordIrrigation = useCallback((fieldId: string, waterUsed: number, duration: number) => {
        const event: IrrigationEvent = {
            id: `event-${Date.now()}`,
            fieldId,
            timestamp: new Date(),
            waterUsed,
            duration,
            method: 'manual',
        };
        setRecentEvents(prev => [event, ...prev].slice(0, 20));

        // Update field's last irrigation
        setFields(prev => prev.map(f =>
            f.id === fieldId ? { ...f, lastIrrigation: new Date(), soilMoisture: Math.min(80, f.soilMoisture + 30) } : f
        ));
    }, []);

    const addSchedule = useCallback((scheduleData: Omit<IrrigationSchedule, 'id'>) => {
        const schedule: IrrigationSchedule = {
            ...scheduleData,
            id: `schedule-${Date.now()}`,
        };
        setSchedules(prev => [...prev, schedule]);
        irrigationReminderService.scheduleReminder(schedule);
    }, []);

    const removeSchedule = useCallback((scheduleId: string) => {
        setSchedules(prev => prev.filter(s => s.id !== scheduleId));
        irrigationReminderService.cancelReminder(scheduleId);
    }, []);

    const toggleSchedule = useCallback((scheduleId: string) => {
        setSchedules(prev => prev.map(s =>
            s.id === scheduleId ? { ...s, isEnabled: !s.isEnabled } : s
        ));
    }, []);

    const getRecommendation = useCallback((fieldId: string) => {
        const field = fields.find(f => f.id === fieldId);
        if (!field) return null;

        const input: IrrigationInput = {
            fieldName: field.name,
            fieldArea: field.area,
            cropType: field.cropType,
            cropStage: field.cropStage,
            soilType: field.soilType,
            lastIrrigationDate: field.lastIrrigation,
            soilMoisture: field.soilMoisture,
            weatherForecast: weather ? {
                temperature: weather.temperature,
                humidity: weather.humidity,
                rainProbability: weather.rainProbability,
            } : undefined,
        };

        return irrigationReminderService.calculateNextIrrigation(input);
    }, [fields, weather]);

    const testNotification = useCallback(() => {
        irrigationReminderService.testNotification();
    }, []);

    const value: IrrigationContextType = {
        // State
        fields,
        selectedFieldId,
        schedules,
        recentEvents,
        weather,
        soundEnabled,
        vibrationEnabled,
        autoScheduleEnabled,
        totalWaterSaved,
        activeReminders: schedules.filter(s => s.isEnabled).length,

        // Actions
        selectField,
        updateFieldMoisture,
        updateFieldCropStage,
        recordIrrigation,
        addSchedule,
        removeSchedule,
        toggleSchedule,
        getRecommendation,
        toggleSound: () => setSoundEnabled(prev => !prev),
        toggleVibration: () => setVibrationEnabled(prev => !prev),
        toggleAutoSchedule: () => setAutoScheduleEnabled(prev => !prev),
        testNotification,
    };

    return (
        <IrrigationContext.Provider value={value}>
            {children}
        </IrrigationContext.Provider>
    );
}

export function useIrrigation() {
    const context = useContext(IrrigationContext);
    if (!context) {
        throw new Error('useIrrigation must be used within an IrrigationProvider');
    }
    return context;
}

export default IrrigationContext;
